import { useEffect, useState } from "react";
import "./SnippetSidebar.css";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import SnippetSwitcher from "./SnippetSwitcher";
import { useSelector } from "react-redux";
import {
  selectSidebarSnippet,
  selectSnippets,
  selectUIStates,
  selectUser,
  setOnDiscardConfirm,
  setSidebarSnippet,
  setUIStates,
} from "../../redux/features/appData";
import { useDispatch } from "react-redux";
import { LinkRecord, Snippet } from "winslow-core";
import { NEW_SNIPPET_ID } from "../ai/AISidebarAsk";
import {
  apiCreateSnippet,
  apiLongerShorter,
  apiUpdateSnippet,
} from "../../services/SnippetService";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import {
  apiCreateSnippetPayload,
  apiUpdateSnippetPayload,
} from "../../services/SnippetService.types";
import { iconPath } from "winslow-core";
import { turnNewlinesIntoHTMLBreaks } from "../shares/ReactHelpers";
import SnippetSidebarMenu from "./SnippetSidebarMenu";
import LongerShorterMenu from "./LongerShorterMenu";
import { SnippetSidebarResource } from "../snippet-sidebar-resource/SnippetSidebarResource";
import { link } from "fs";

const FIRST_NAME = "Name";

type SnippetSidebarProps = {
  allowSelect?: boolean;
};

export const SnippetSidebar = ({ allowSelect = true }: SnippetSidebarProps) => {
  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader(true);

  const storeUser = useSelector(selectUser);
  const snippets = useSelector(selectSnippets);
  const uiStates = useSelector(selectUIStates);
  const sidebarSnippet = useSelector(selectSidebarSnippet);

  const [originalSnippetManager, setOriginalSnippetManager] = useState<{
    originalAnswer: string;
    longerShorter: "longer" | "shorter" | null;
  }>({
    originalAnswer: sidebarSnippet?.answer || "",
    longerShorter: null,
  });

  const [snippetEmail, setSnippetEmail] = useState<string>("");
  const [snippetTitle, setSnippetTitle] = useState<string>("");

  useEffect(() => {
    if (!snippets) {
      return;
    }

    if (sidebarSnippet) {
      let editted =
        originalSnippetManager.longerShorter === "longer" ||
        originalSnippetManager.longerShorter === "shorter"
          ? true
          : false;

      setSnippetEmail(buildEmail(sidebarSnippet));
      setSnippetTitle(sidebarSnippet.name);
      dispatch(
        setUIStates({
          ...uiStates,
          snippetEdited: editted,
        })
      );
    }
  }, [sidebarSnippet]);

  const buildEmail = (snippet: Snippet): string => {
    let email = "";

    // email += "Hi [Name]\n\n";
    email += snippet.answer + "\n\n";
    // email += "Sincerely,\n[Your Name]";

    return email;
  };

  const handleCopyEmail = () => {
    const greetingPhrase =
      (storeUser?.greetingPhrase ? storeUser.greetingPhrase + " " : "Hi ") +
      "[" +
      FIRST_NAME +
      "],";

    const compiledText =
      greetingPhrase +
      "\n\n" +
      snippetEmail.trim() +
      "\n\n" +
      storeUser?.signatureBlock;

    navigator.clipboard.writeText(compiledText).then(
      () => {
        toast("Copied!", toastSuccess);
      },
      (err) => {
        toast("Failed to copy!", toastFail);
      }
    );
  };

  const handleUpdateSnippet = async () => {
    if (!sidebarSnippet) {
      return;
    }

    const updatedSnippet = {
      // id: sidebarSnippet?.id,
      // collectionId: sidebarSnippet?.collectionId,
      // userId: storeUser?.id,
      name: snippetTitle,
      answer: snippetEmail,
      question: sidebarSnippet?.question,
      // linkRecordIds: [] as string[],
    } as apiUpdateSnippetPayload;

    const snippet = await apiUpdateSnippet(sidebarSnippet?.id, updatedSnippet);

    if (!snippet) {
      toast("Problem updating snippet.", toastFail);
      return;
    }

    console.log("[SnippetSidebar] updated snippet: ", snippet);
    dispatch(setSidebarSnippet({ ...snippet }));

    toast("Updated.", toastSuccess);
    reloadAll(storeUser?.currentWorkspaceId);
  };

  const handleSaveNewSnippet = async () => {
    console.log("[handleSaveNewSnippet] sidebar snippr: ", sidebarSnippet);

    const newSnippet = {
      collectionId: sidebarSnippet?.collectionId,
      userId: storeUser?.id,
      name: snippetTitle,
      answer: snippetEmail,
      question: sidebarSnippet?.question,
      linkRecordIds: sidebarSnippet?.linkRecords
        ? sidebarSnippet.linkRecords.map((linkRecord: LinkRecord) => {
            return linkRecord.id;
          })
        : ([] as string[]),
    } as apiCreateSnippetPayload;

    console.log("[handleSaveNewSnippet] newSnippet payload: ", newSnippet);

    const snippet = await apiCreateSnippet(newSnippet);

    if (!snippet) {
      toast("Problem creating snippet.", toastFail);
      return;
    }

    toast("Saved.", toastSuccess);
    dispatch(setSidebarSnippet(snippet));
    reloadAll(storeUser?.currentWorkspaceId);
  };

  const handleDuplicate = () => {
    if (!sidebarSnippet) {
      return;
    }

    const newSnippet = {
      ...sidebarSnippet,
      id: NEW_SNIPPET_ID,
      name: "Copy of " + sidebarSnippet.name,
    };

    dispatch(setSidebarSnippet(newSnippet));
    toast("Duplicate copy created.", toastSuccess);
  };

  const handleTitleChange = (e: any) => {
    setSnippetTitle(e.target.value);
    dispatch(
      setUIStates({
        ...uiStates,
        snippetEdited: true,
      })
    );
  };

  const handleEmailChange = (e: any) => {
    setSnippetEmail(e.target.value);
    dispatch(
      setUIStates({
        ...uiStates,
        snippetEdited: true,
      })
    );
  };

  const handleClose = () => {
    dispatch(
      setUIStates({
        ...uiStates,
        snippetSidebarOpen: false,
        snippetEdited: false,
      })
    );

    dispatch(setSidebarSnippet(null));
  };

  const countSnippetLinks = (snippet: Snippet): number => {
    let count = 0;

    snippet?.linkRecords.forEach((linkRecord: LinkRecord) => {
      if (linkRecord.links && linkRecord.links.length > 0) {
        if (linkRecord.links[0].appId !== "FILE") {
          count++;
        }
      }
    });

    return count;
  };

  const isNewSnippet = sidebarSnippet?.id === NEW_SNIPPET_ID;

  console.log("[SnippetSidebar] snippet: ", sidebarSnippet);

  return (
    <>
      <div id="snippet-sidebar">
        {isNewSnippet && (
          <div className="snippet-sidebar-new-warning">
            This is a new quick reply, please remember to save it if you want to
            reuse it.
          </div>
        )}
        <div className="snippet-sidebar-header">
          <div className="snippet-sidebar-left">Quick Reply</div>
          <div
            className="snippet-sidebar-close"
            onClick={() => {
              if (uiStates.snippetEdited) {
                dispatch(setOnDiscardConfirm(handleClose));
                dispatch(
                  setUIStates({
                    ...uiStates,
                    confirmDiscardSnippetOpen: true,
                  })
                );
              } else {
                handleClose();
              }
            }}>
            <img className="icon-close" alt="" />
          </div>
        </div>

        <div className="snippet-sidebar-subheader">
          <div className="snippet-sidebar-title">
            <input
              type="text"
              value={snippetTitle}
              onChange={handleTitleChange}
              placeholder="Name your snippet"
            />
          </div>
          {allowSelect && (
            <div className="snippet-sidebar-snippet-menu">
              {snippets && snippets.length > 1 && sidebarSnippet && (
                <SnippetSwitcher
                  snippets={snippets}
                  selectedSnippetId={sidebarSnippet.id}
                />
              )}
            </div>
          )}
        </div>

        <div className="snippet-sidebar-resources">
          {sidebarSnippet &&
            sidebarSnippet.linkRecords &&
            sidebarSnippet.linkRecords.length > 0 &&
            sidebarSnippet.linkRecords.map((linkRecord: LinkRecord) => {
              return (
                <SnippetSidebarResource
                  key={"sidebar-resource-" + linkRecord.id}
                  linkRecord={linkRecord}
                />
              );
            })}
        </div>

        <div className="snippet-sidebar-body">
          <div className="snippet-greeting">
            <div>
              {storeUser?.greetingPhrase
                ? storeUser?.greetingPhrase + " "
                : "Hi "}
              [First Name],
            </div>
            <div className="snippet-longer-shorter-menu">
              {sidebarSnippet && (
                <LongerShorterMenu
                  snippet={sidebarSnippet}
                  usedOption={originalSnippetManager.longerShorter}
                  onRevert={() => {
                    dispatch(
                      setSidebarSnippet({
                        ...sidebarSnippet,
                        answer: originalSnippetManager.originalAnswer,
                      })
                    );
                    setOriginalSnippetManager({
                      originalAnswer: sidebarSnippet?.answer || "",
                      longerShorter: null,
                    });
                  }}
                  onLonger={async () => {
                    const result = await apiLongerShorter(
                      "longer",
                      sidebarSnippet.answer
                    );
                    if (result) {
                      setOriginalSnippetManager({
                        originalAnswer: sidebarSnippet?.answer || "",
                        longerShorter: "longer",
                      });
                      dispatch(
                        setSidebarSnippet({ ...sidebarSnippet, answer: result })
                      );
                      dispatch(
                        setUIStates({
                          ...uiStates,
                          snippetEditted: true,
                        })
                      );
                    }
                  }}
                  onShorter={async () => {
                    const result = await apiLongerShorter(
                      "shorter",
                      sidebarSnippet.answer
                    );
                    if (result) {
                      setOriginalSnippetManager({
                        originalAnswer: sidebarSnippet?.answer || "",
                        longerShorter: "longer",
                      });
                      dispatch(
                        setSidebarSnippet({ ...sidebarSnippet, answer: result })
                      );
                      dispatch(
                        setUIStates({
                          ...uiStates,
                          snippetEditted: true,
                        })
                      );
                    }
                  }}
                />
              )}
            </div>
          </div>

          <div className="snippet-sidebar-email">
            <textarea
              value={snippetEmail}
              onChange={handleEmailChange}
              placeholder={
                "Enter the body of the email you wish to use.\n\n" +
                "You can use the following in your email to replace\n" +
                "common values dynamically:\n\n" +
                "[Recipient] for the recipient's first name\n" +
                "[Sender] for the senders first name\n\n"
              }
            />
          </div>

          <div className="snippet-signature">
            <>{turnNewlinesIntoHTMLBreaks(storeUser?.signatureBlock)}</>
          </div>
        </div>

        <div className="snippet-footer">
          <div className="snippet-footer-buttons">
            {!isNewSnippet && (
              <button
                onClick={handleUpdateSnippet}
                disabled={!uiStates.snippetEdited || snippetTitle.length < 1}
                id="snippet-footer-buttons-save">
                Save Changes
              </button>
            )}
            {isNewSnippet && (
              <button
                onClick={handleSaveNewSnippet}
                id="snippet-footer-buttons-save"
                disabled={snippetTitle.length < 1}
                // style={{ backgroundColor: "#2E69D4", color: "#FFF" }}
              >
                Save
              </button>
            )}

            <div
              className="snippet-footer-button-circle"
              onClick={handleCopyEmail}>
              <img src={iconPath("copy.svg", storeUser)} alt="" />
            </div>

            {sidebarSnippet &&
              (sidebarSnippet.id !== NEW_SNIPPET_ID ||
                countSnippetLinks(sidebarSnippet) > 0) && (
                <div className="snippet-footer-button-circle">
                  <SnippetSidebarMenu
                    snippet={sidebarSnippet}
                    onDuplicate={() => {
                      if (uiStates.snippetEdited) {
                        dispatch(setOnDiscardConfirm(handleDuplicate));
                        dispatch(
                          setUIStates({
                            ...uiStates,
                            confirmDiscardSnippetOpen: true,
                          })
                        );
                      } else {
                        handleDuplicate();
                      }
                    }}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};
